<template>
  <div class="express-settings-container">
    <h2 class="page-title">收发货配置</h2>
    
    <!-- 地址设置 -->
    <div class="tabs-container">
      <div class="address-form">
        <el-card shadow="hover" class="address-card">
          <template #header>
            <div class="card-header">
              <span>发货地址设置</span>
              <el-button 
                type="primary" 
                size="small" 
                @click="handleEdit"
                :loading="loading"
              >
                {{ shippingAddress.id ? '编辑' : '新增' }}
              </el-button>
            </div>
          </template>
          <div v-if="shippingAddress.id" class="address-info">
            <div class="info-item">
              <span class="info-label">公司名称：</span>
              <span class="info-value">{{ shippingAddress.company_name }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">联系人：</span>
              <span class="info-value">{{ shippingAddress.name }} {{ shippingAddress.mobile }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">地址：</span>
              <span class="info-value">{{ shippingAddress.province }} {{ shippingAddress.city }} {{ shippingAddress.district }} {{ shippingAddress.address }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">状态：</span>
              <el-tag :type="shippingAddress.status ? 'success' : 'danger'">
                {{ shippingAddress.status ? '启用' : '禁用' }}
              </el-tag>
            </div>
          </div>
          <div v-else class="empty-address">
            <el-empty description="暂无发货地址，请点击新增按钮添加"></el-empty>
          </div>
        </el-card>
      </div>
    </div>
    
    <!-- 编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="500px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <el-form
        ref="addressFormRef"
        :model="formData"
        :rules="formRules"
        label-position="top"
        label-width="100px"
        class="address-form"
      >
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="公司名称" prop="company_name">
              <el-input 
                v-model="formData.company_name" 
                placeholder="请输入公司名称" 
                clearable
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系人" prop="name">
              <el-input 
                v-model="formData.name" 
                placeholder="请输入联系人姓名" 
                clearable
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="手机号" prop="mobile">
              <el-input 
                v-model="formData.mobile" 
                placeholder="请输入手机号" 
                clearable
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <div style="display: flex; gap: 10px; margin-bottom: 20px;">
              <el-form-item label="省份" prop="province" style="flex: 1; margin-bottom: 0;">
                <el-select 
                  v-model="formData.province" 
                  placeholder="请选择省份" 
                  style="width: 100%;"
                >
                  <el-option
                    v-for="province in provinces"
                    :key="province.id"
                    :label="province.name"
                    :value="province.name"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label="城市" prop="city" style="flex: 1; margin-bottom: 0;">
                <el-select 
                  v-model="formData.city" 
                  placeholder="请选择城市" 
                  style="width: 100%;"
                >
                  <el-option
                    v-for="city in cities"
                    :key="city.id"
                    :label="city.name"
                    :value="city.name"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label="区县" prop="district" style="flex: 1; margin-bottom: 0;">
                <el-select 
                  v-model="formData.district" 
                  placeholder="请选择区县" 
                  style="width: 100%;"
                >
                  <el-option
                    v-for="district in districts"
                    :key="district.id"
                    :label="district.name"
                    :value="district.name"
                  />
                </el-select>
              </el-form-item>
            </div>
          </el-col>
          <el-col :span="24">
            <el-form-item label="详细地址" prop="address">
              <el-input 
                v-model="formData.address" 
                placeholder="请输入详细地址" 
                type="textarea"
                :rows="3"
                clearable
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="状态" prop="status">
              <el-radio-group v-model="formData.status">
                <el-radio :label="1">启用</el-radio>
                <el-radio :label="0">禁用</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button 
            type="primary" 
            @click="handleSubmit"
            :loading="loading"
          >
            {{ formData.id ? '保存' : '新增' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { 
  getExpressSettings, 
  createExpressSetting, 
  updateExpressSetting, 
  deleteExpressSetting 
} from '@/api/expressSettings'
import service from '@/api/axios'

export default {
  name: 'ExpressSettings',
  setup() {
    // 响应式数据
    const loading = ref(false)
    const dialogVisible = ref(false)
    const addressFormRef = ref(null)
    
    // 省市区数据
    const provinces = ref([])
    const cities = ref([])
    const districts = ref([])
    
    // 地址数据
    const shippingAddress = ref({
      id: null,
      company_name: '',
      name: '',
      mobile: '',
      province: '',
      city: '',
      district: '',
      address: '',
      status: 1,
      type: 1
    })
    
    // 表单数据
    const formData = reactive({
      id: null,
      company_name: '',
      name: '',
      mobile: '',
      province: '',
      city: '',
      district: '',
      address: '',
      status: 1,
      type: 1
    })
    
    // 表单验证规则
    const formRules = {
      company_name: [
        { required: true, message: '请输入公司名称', trigger: 'blur' }
      ],
      name: [
        { required: true, message: '请输入联系人姓名', trigger: 'blur' }
      ],
      mobile: [
        { required: true, message: '请输入手机号', trigger: 'blur' }
      ],
      province: [
        { required: true, message: '请选择省份', trigger: 'change' }
      ],
      city: [
        { required: true, message: '请选择城市', trigger: 'change' }
      ],
      district: [
        { required: true, message: '请选择区县', trigger: 'change' }
      ],
      address: [
        { required: true, message: '请输入详细地址', trigger: 'blur' }
      ]
    }
    
    // 计算属性：弹窗标题
    const dialogTitle = computed(() => {
      return '编辑发货地址'
    })
    
    // 获取省份列表
    const fetchProvinces = async () => {
      try {
        const response = await service({
          url: '/manage/provinces',
          method: 'get'
        })
        provinces.value = response.data
      } catch (error) {
        ElMessage.error('获取省份列表失败')
        console.error(error)
        // 如果接口调用失败，使用默认数据
        provinces.value = [{ id: 1, name: '广东省' }, { id: 2, name: '北京市' }, { id: 3, name: '上海市' }]
      }
    }
    
    // 获取城市列表
    const fetchCities = async (provinceId) => {
      try {
        const response = await service({
          url: '/manage/cities',
          method: 'get',
          params: { province_id: provinceId }
        })
        cities.value = response.data
      } catch (error) {
        ElMessage.error('获取城市列表失败')
        console.error(error)
        // 如果接口调用失败，使用默认数据
        cities.value = [{ id: 1, name: '广州市' }, { id: 2, name: '深圳市' }]
      }
    }
    
    // 获取区县列表
    const fetchDistricts = async (cityId) => {
      try {
        const response = await service({
          url: '/manage/districts',
          method: 'get',
          params: { city_id: cityId }
        })
        districts.value = response.data
      } catch (error) {
        ElMessage.error('获取区县列表失败')
        console.error(error)
        // 如果接口调用失败，使用默认数据
        districts.value = [{ id: 1, name: '天河区' }, { id: 2, name: '越秀区' }]
      }
    }
    
    // 监听省份变化，更新城市列表
    watch(() => formData.province, (newProvince, oldProvince) => {
      // 当省份从有值变为其他值，或者从空变为有值时，重置城市和区县
      // 避免在初始化表单数据时触发重置
      if (newProvince && (newProvince !== oldProvince || !oldProvince)) {
        // 重置城市和区县
        formData.city = ''
        formData.district = ''
        cities.value = []
        districts.value = []
        
        // 获取对应的省份ID
        const province = provinces.value.find(p => p.name === newProvince)
        if (province) {
          fetchCities(province.id)
        }
      } else if (!newProvince && oldProvince) {
        // 当省份从有值变为空值时，重置城市和区县
        cities.value = []
        districts.value = []
      }
    })
    
    // 监听城市变化，更新区县列表
    watch(() => formData.city, (newCity, oldCity) => {
      // 当城市从有值变为其他值，或者从空变为有值时，重置区县并获取区县列表
      // 避免在初始化表单数据时触发重置
      if (newCity && (newCity !== oldCity || !oldCity)) {
        // 重置区县
        formData.district = ''
        districts.value = []
        
        // 获取对应的城市ID
        const city = cities.value.find(c => c.name === newCity)
        if (city) {
          fetchDistricts(city.id)
        }
      } else if (!newCity && oldCity) {
        // 当城市从有值变为空值时，重置区县
        districts.value = []
      }
    })
    
    // 初始化加载数据
    const loadData = async () => {
      loading.value = true
      try {
        // 获取发货地址
        const shippingRes = await getExpressSettings({ type: 1, page: 1, pageSize: 1 })
        if (shippingRes.code === 200 && shippingRes.data.list.length > 0) {
          shippingAddress.value = shippingRes.data.list[0]
        }
      } catch (error) {
        ElMessage.error('加载数据失败：' + (error.response?.data?.message || error.message))
      } finally {
        loading.value = false
      }
    }
    
    // 处理编辑
    const handleEdit = async () => {
      // 重置表单
      Object.assign(formData, {
        id: null,
        company_name: '',
        name: '',
        mobile: '',
        province: '',
        city: '',
        district: '',
        address: '',
        status: 1,
        type: 1
      })
      
      // 重置省市区数据
      cities.value = []
      districts.value = []
      
      // 确保省份数据已加载
      if (provinces.value.length === 0) {
        await fetchProvinces()
      }
      
      // 填充现有数据
      let addressData = null
      if (shippingAddress.value.id) {
        addressData = shippingAddress.value
      }
      
      if (addressData) {
        // 保存当前地址的所有数据
        const { id, company_name, name, mobile, province, city, district, address, status, type } = addressData
        
        // 保存当前地址的省市区名称
        const currentProvince = province
        const currentCity = city
        const currentDistrict = district
        
        // 先获取省市区数据，再填充表单
        let provinceObj = null
        let cityObj = null
        
        // 临时存储省市区数据，避免触发watch监听器
        let tempProvinces = [...provinces.value]
        let tempCities = []
        let tempDistricts = []
        
        // 如果有省份，获取对应的城市列表
        if (currentProvince) {
          // 查找匹配的省份
          provinceObj = tempProvinces.find(p => p.name === currentProvince)
          if (provinceObj) {
            // 获取城市列表
            try {
              const response = await service({
                url: '/manage/cities',
                method: 'get',
                params: { province_id: provinceObj.id }
              })
              tempCities = response.data
            } catch (error) {
              console.error('获取城市列表失败:', error)
              tempCities = [{ id: 1, name: '广州市' }, { id: 2, name: '深圳市' }]
            }
            
            // 如果有城市，获取对应的区县列表
            if (currentCity) {
              // 查找匹配的城市
              cityObj = tempCities.find(c => c.name === currentCity)
              if (cityObj) {
                // 获取区县列表
                try {
                  const response = await service({
                    url: '/manage/districts',
                    method: 'get',
                    params: { city_id: cityObj.id }
                  })
                  tempDistricts = response.data
                } catch (error) {
                  console.error('获取区县列表失败:', error)
                  tempDistricts = [{ id: 1, name: '天河区' }, { id: 2, name: '越秀区' }]
                }
              } else {
                // 如果没找到匹配的城市，手动添加到城市列表
                tempCities.push({ id: 0, name: currentCity })
              }
            }
          } else {
            // 如果没找到匹配的省份，手动添加到省份列表
            tempProvinces.push({ id: 0, name: currentProvince })
          }
        }
        
        // 如果有区县但区县列表为空，手动添加到区县列表
        if (currentDistrict && tempDistricts.length === 0) {
          tempDistricts.push({ id: 0, name: currentDistrict })
        }
        
        // 更新省市区数据
        provinces.value = tempProvinces
        cities.value = tempCities
        districts.value = tempDistricts
        
        // 现在才填充表单数据，避免触发watch监听器重置数据
        formData.id = id
        formData.company_name = company_name
        formData.name = name
        formData.mobile = mobile
        formData.address = address
        formData.status = status
        formData.type = type
        
        // 最后填充省市区，避免触发watch监听器
        formData.province = currentProvince
        formData.city = currentCity
        formData.district = currentDistrict
      }
      
      dialogVisible.value = true
    }
    
    // 处理提交
    const handleSubmit = async () => {
      // 表单验证
      if (!addressFormRef.value) return
      await addressFormRef.value.validate((valid) => {
        if (!valid) return false
      })
      
      loading.value = true
      try {
        let res
        if (formData.id) {
          // 更新操作
          res = await updateExpressSetting(formData.id, formData)
        } else {
          // 新增操作
          res = await createExpressSetting(formData)
        }
        
        if (res.code === 200 || res.code === 201) {
          ElMessage.success(formData.id ? '更新成功' : '新增成功')
          dialogVisible.value = false
          loadData() // 重新加载数据
        } else {
          ElMessage.error(res.message || '操作失败')
        }
      } catch (error) {
        ElMessage.error('操作失败：' + (error.response?.data?.message || error.message))
      } finally {
        loading.value = false
      }
    }
    
    // 组件挂载时加载数据
    onMounted(() => {
      loadData()
      fetchProvinces()
    })
    
    return {
      loading,
      dialogVisible,
      addressFormRef,
      shippingAddress,
      formData,
      formRules,
      dialogTitle,
      provinces,
      cities,
      districts,
      handleEdit,
      handleSubmit
    }
  }
}
</script>

<style scoped>
.express-settings-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 60px);
}

.page-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #303133;
}

.tabs-container {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.address-card {
  margin-top: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.address-info {
  padding: 10px 0;
}

.info-item {
  margin-bottom: 15px;
  display: flex;
  align-items: flex-start;
}

.info-label {
  width: 100px;
  font-weight: bold;
  color: #606266;
  margin-right: 10px;
  text-align: right;
}

.info-value {
  flex: 1;
  color: #303133;
  word-break: break-all;
}

.empty-address {
  padding: 30px 0;
  text-align: center;
}

.address-form {
  margin-top: 20px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
}
</style>
